import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet';

import { InputWithConfirm, RadioOptionsWithConfirm, SelectWithOtherWithConfirm } from 'shared-components';
import { ENGLISH_TEST_TYPES, QUALIFICATIONS } from 'data';

import { Q } from '../../../../store/student/types';
import { ConfigField } from '../../../../types';
import { ENGLISH_SPEAKING_COUNTRIES } from 'data';

import QuestionPage from '../../../templates/question';
import { getImportOffer } from '../../../../utilities/helpers';
import { titleAppend } from '../../../../utilities/head';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as StudentActions from '../../../../store/student/actions';
import * as PIActions from '../../../../store/student/personalInfo/actions';
import {
  createNavigateForwardAction,
  createNavigateBackwardAction,
  createNavigateToAction
} from '../../../../middleware/actions';
import { getPageStatus } from './utils';

import { Pages } from '../../../../store/student/personalInfo/types';
import { toggleTaskMenu } from '../../../../store/ui/actions';
import { ApplicationState } from '../../../../store';

export interface IDispatchProps {
  onPrevious: () => void;
  onNext: (config) => void;
  onSkip: () => void;
  onChange: (fieldName: string, value: boolean | undefined | string | number) => void;
  onBackToTaskList: () => void;
}

const charsValueMap: { [key: string]: string } = {
  '1': 'English speaking country',
  '2': 'Previous test',
  '3': 'Previous visa application',
  '4': 'US study abroad',
  '5': 'Gifted student',
  '6': 'Other'
};

const useData = () => {
  const Page6 = useSelector((state: ApplicationState) => state.config.Checklist.PersonalInfo.Page6);
  const config = useSelector((state: ApplicationState) => state.config);
  const student = useSelector((state: ApplicationState) => state.student);
  const personalInfo = useSelector((state: ApplicationState) => state.student.personalInfo);

  const {
    HasEnglishTest: hasEnglishTestConfig,
    EnglishTestType: englishTestTypeConfig,
    EnglishTestTypeOther: englishTestTypeOtherConfig,
    EnglishTestNotReqdReason: englishTestNotReqdReasonConfig,
    EnglishTestNotReqdReasonOther: englishTestNotReqdReasonOtherConfig,
    Qualification: qualificationConfig,
    QualificationOther: qualificationOtherConfig
  }: {
    HasEnglishTest: ConfigField;
    EnglishTestType: ConfigField;
    EnglishTestTypeOther: ConfigField;
    EnglishTestNotReqdReason: ConfigField;
    EnglishTestNotReqdReasonOther: ConfigField;
    Qualification: ConfigField;
    QualificationOther: ConfigField;
  } = Page6;

  const {
    hasEnglishTest = hasEnglishTestConfig?.defaultValue,
    englishTestType = englishTestTypeConfig?.defaultValue,
    englishTestTypeOther = englishTestTypeOtherConfig?.defaultValue,
    englishTestNotReqdReason = englishTestNotReqdReasonConfig?.defaultValue,
    englishTestNotReqdReasonOther = englishTestNotReqdReasonOtherConfig?.defaultValue,
    qualification = qualificationConfig?.defaultValue,
    qualificationOther = qualificationOtherConfig?.defaultValue
  } = personalInfo;

  const { complete, visible } = getPageStatus(config, {
    hasEnglishTest,
    englishTestType,
    englishTestTypeOther,
    englishTestNotReqdReasonOther,
    qualification,
    qualificationOther,
    englishTestNotReqdReason
  });

  const {
    hasEnglishTest: hasEnglishTestVisible,
    englishTestType: englishTestTypeVisible,
    englishTestTypeOther: englishTestTypeOtherVisible,
    englishTestNotReqdReason: englishTestNotReqdReasonVisible,
    qualification: qualificationVisible,
    qualificationOther: qualificationOtherVisible
  } = visible;

  return {
    sectionTitle: config.Checklist.PersonalInfo.shortTitle,
    complete,
    currentStep: 6,
    steps: 10,
    hasEnglishTest,
    hasEnglishTestVisible,
    hasEnglishTestConfig,
    englishTestType,
    englishTestTypeVisible,
    englishTestNotReqdReasonConfig,
    englishTestNotReqdReason,
    englishTestNotReqdReasonVisible,
    englishTestNotReqdReasonOther,
    englishTestNotReqdReasonOtherConfig,
    qualificationVisible,
    qualificationOtherVisible,
    qualificationConfig,
    qualificationOtherConfig,
    qualificationOther,
    qualification,
    englishTestTypeConfig,
    englishTestTypeOther,
    englishTestTypeOtherVisible,
    englishTestTypeOtherConfig,
    config: config,
    isPageComplete: student.offer.isPIPage6Complete,
    offer: student.offer,
    importData: student.importData,
    isStudentFromEnglishSpeakingCountry: ENGLISH_SPEAKING_COUNTRIES.some(c => c.includes(student.summary.nationality))
  };
};

const getImportUpdateFields = data => {
  const importUpdateEnabled = [];
  const importOffer = getImportOffer(data.importData, data.offer.intakeId, data.offer.offerId);
  if (data.isPageComplete) {
    if (importOffer?.isSELTReqd !== undefined && data.hasEnglishTest !== importOffer?.isSELTReqd) {
      importUpdateEnabled.push(Q.HAS_ENGLISH_TEST);
    }
    if (
      data.importData.englishTestProvider &&
      data.englishTestType !== data.importData.englishTestProvider &&
      data.hasEnglishTest
    ) {
      importUpdateEnabled.push(Q.ENGLISH_TEST_TYPE);
    }
  }
  return importUpdateEnabled;
};

const getImportApprovedFields = data => {
  const importOffer = getImportOffer(data.importData, data.offer.intakeId, data.offer.offerId);
  const importApproved = [];
  if (importOffer?.isSELTReqd !== undefined && importOffer?.isSELTReqd === data.hasEnglishTest) {
    importApproved.push(Q.HAS_ENGLISH_TEST);
  }
  if (data.isPageComplete) {
    if (data.importData?.englishTestProvider === data.englishTestType && data.hasEnglishTest) {
      importApproved.push(Q.ENGLISH_TEST_TYPE);
    }
  }
  return importApproved;
};

const PersonalQPage6 = () => {
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = config => {
    dispatch(StudentActions.savePersonalInfo(Pages.PAGE6, config));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = useCallback(
    (fieldName: string, value, refValue?) => dispatch(PIActions.createUpdatePIFieldAction(fieldName, value, refValue)),
    [dispatch]
  );
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const data = useData();
  const importOffer = getImportOffer(data.importData, data.offer.intakeId, data.offer.offerId);

  const [hasEnglishTest, sethHasEnglishTest] = useState(data.hasEnglishTest);
  const [englishTestType, setEnglishTestType] = useState(data.englishTestType);
  const [_otherEnglishTestType, setOtherEnglishTestType] = useState(data.englishTestType);
  const [importApproved, setImportApproved] = useState(getImportApprovedFields(data));
  const [importUpdateEnabled, _setImportUpdateEnabled] = useState(getImportUpdateFields(data));
  const [importEdited, setImportEdited] = useState([]);

  useEffect(() => {
    sethHasEnglishTest(data.hasEnglishTest);
    setEnglishTestType(data.englishTestType);
    setOtherEnglishTestType(data.englishTestType);
  }, [data.englishTestType, data.hasEnglishTest]);

  useEffect(() => {
    if (importOffer?.isSELTReqd && data.hasEnglishTest === undefined) {
      onChange(Q.HAS_ENGLISH_TEST, importOffer?.isSELTReqd);
      setImportApproved(prevState => [...prevState, Q.HAS_ENGLISH_TEST]);
    }
  }, [data.hasEnglishTest, importOffer?.isSELTReqd, onChange]);

  const handleYesNoClick = (target, question) => {
    onChange(target, question.char === 'Y' ? true : false);
    if (question.char === 'Y' && target === Q.HAS_ENGLISH_TEST) {
      setEnglishTestType(prevState => (isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE) ? prevState.englishTestType : ''));
      setImportApproved(getImportApprovedFields(data));
      onChange(Q.ENGLISH_TEST_TYPE, isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE) ? englishTestType : '');
      onChange(Q.ENGLISH_TEST_NOT_REQD_REASON, '');
      onChange(Q.ENGLISH_TEST_NOT_REQD_REASON_OTHER, '');
      onChange(Q.QUALIFICATION, '');
      onChange(Q.QUALIFICATION_OTHER, '');
    }
  };

  const isImportEnabled = target => {
    switch (target) {
      case Q.HAS_ENGLISH_TEST:
        return data.hasEnglishTestVisible && importOffer?.isSELTReqd !== undefined;
      case Q.ENGLISH_TEST_TYPE:
        return data.englishTestTypeVisible && !!data.importData.englishTestProvider;
      default:
        return false;
    }
  };

  const isImportUpdateEnabled = target => {
    if (!data.isPageComplete || !data.complete) return false;
    switch (target) {
      case Q.HAS_ENGLISH_TEST:
        return data.hasEnglishTest !== undefined && importUpdateEnabled.includes(target);
      case Q.ENGLISH_TEST_TYPE:
        return !!data.englishTestType && importUpdateEnabled.includes(target);
      default:
        return false;
    }
  };

  const isImportComplete = target => {
    if (!isImportEnabled(target) || isImportUpdateEnabled(target)) return true;
    return importApproved.includes(target) || importEdited.includes(target);
  };

  const getComplete = () => {
    const importQuestions = [Q.HAS_ENGLISH_TEST, Q.ENGLISH_TEST_TYPE];
    const importComplete = importQuestions.every(q => isImportComplete(q));
    return data.complete && importComplete;
  };

  const handleImportApprove = target => {
    setImportApproved(prevState => [...prevState, target]);
    setImportEdited(prevState => prevState.filter(q => q !== target));
  };

  const handleImportEdit = target => {
    setImportApproved(prevState => prevState.filter(q => q !== target));
    setImportEdited(prevState => [...prevState, target]);
  };

  const handleImportUndo = target => {
    setImportApproved(prevState => prevState.filter(q => q !== target));
    setImportEdited(prevState => prevState.filter(q => q !== target));
  };

  const handleQualificationOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Q.QUALIFICATION, e.currentTarget.value);
    if (data.qualificationOther) {
      onChange(Q.QUALIFICATION_OTHER, '');
    }
  };

  const notReqdReasons = [
    {
      label: 'My English was assessed based on my previous qualification',
      selected: data.englishTestNotReqdReason === charsValueMap['2']
    },
    {
      label: 'I have met the English language requirement in my previous visa application',
      selected: data.englishTestNotReqdReason === charsValueMap['3']
    },
    {
      label: "I'm coming as a part of US study abroad programme",
      selected: data.englishTestNotReqdReason === charsValueMap['4']
    },
    {
      label: "I'm a gifted student",
      selected: data.englishTestNotReqdReason === charsValueMap['5']
    },
    {
      label: "Other/I don't know",
      selected: data.englishTestNotReqdReason === charsValueMap['6']
    }
  ];

  if (data.isStudentFromEnglishSpeakingCountry) {
    notReqdReasons.unshift({
      label: 'I am a national of a majority English-speaking country',
      selected: data.englishTestNotReqdReason === charsValueMap['1']
    });
  }

  return (
    <QuestionPage
      complete={getComplete()}
      currentStep={data.currentStep}
      steps={data.steps}
      onNext={() => onNext(data.config)}
      onSkip={onSkip}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
      section={data.sectionTitle}
    >
      <Helmet>
        <title>
          {`${data.sectionTitle}: Step ${data.currentStep}`} {titleAppend}
        </title>
      </Helmet>

      {data.hasEnglishTestVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_ENGLISH_TEST}
          id={Q.HAS_ENGLISH_TEST}
          value={data.hasEnglishTest ? 'Yes' : 'No'}
          label={data.hasEnglishTestConfig?.label}
          inputHelperText={data.hasEnglishTestConfig?.helpText}
          onClick={question => handleYesNoClick(Q.HAS_ENGLISH_TEST, question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: data.hasEnglishTest === true
            },
            {
              char: 'N',
              label: 'No',
              selected: data.hasEnglishTest === false
            }
          ]}
          importEnabled={isImportEnabled(Q.HAS_ENGLISH_TEST)}
          importValue={importOffer?.isSELTReqd ? 'Yes' : 'No'}
          importUpdateEnabled={isImportUpdateEnabled(Q.HAS_ENGLISH_TEST)}
          importApproved={importApproved.includes(Q.HAS_ENGLISH_TEST)}
          importEdited={importEdited.includes(Q.HAS_ENGLISH_TEST)}
          onImportApprove={() => {
            handleImportApprove(Q.HAS_ENGLISH_TEST);
            onChange(Q.HAS_ENGLISH_TEST, importOffer?.isSELTReqd);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.HAS_ENGLISH_TEST);
            onChange(
              Q.HAS_ENGLISH_TEST,
              isImportUpdateEnabled(Q.HAS_ENGLISH_TEST) ? hasEnglishTest : importOffer?.isSELTReqd
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.HAS_ENGLISH_TEST);
            onChange(Q.HAS_ENGLISH_TEST, isImportUpdateEnabled(Q.HAS_ENGLISH_TEST) ? hasEnglishTest : '');
          }}
        />
      )}

      {data.englishTestTypeVisible && (
        <SelectWithOtherWithConfirm
          key={Q.ENGLISH_TEST_TYPE}
          id={Q.ENGLISH_TEST_TYPE}
          label={data.englishTestTypeConfig?.label}
          Other
          options={ENGLISH_TEST_TYPES}
          otherOptions={[
            {
              label: 'Other',
              value: 'Other'
            }
          ]}
          otherOptionLabel={data.englishTestTypeOtherConfig?.label}
          value={data.englishTestType}
          onChange={evt => onChange(Q.ENGLISH_TEST_TYPE, evt.currentTarget.value)}
          onChangeOtherText={evt => onChange(Q.ENGLISH_TEST_TYPE_OTHER, evt.currentTarget.value)}
          inputHelperText={data.englishTestTypeConfig?.helpText}
          otherValue={data.englishTestTypeOther}
          disableOther={false}
          importEnabled={isImportEnabled(Q.ENGLISH_TEST_TYPE)}
          importValue={data.importData.englishTestProvider}
          importUpdateEnabled={isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE)}
          importApproved={importApproved.includes(Q.ENGLISH_TEST_TYPE)}
          importEdited={importEdited.includes(Q.ENGLISH_TEST_TYPE)}
          onImportApprove={() => {
            handleImportApprove(Q.ENGLISH_TEST_TYPE);
            onChange(Q.ENGLISH_TEST_TYPE, data.importData.englishTestProvider);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.ENGLISH_TEST_TYPE);
            onChange(
              Q.ENGLISH_TEST_TYPE,
              isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE) ? data.englishTestType : data.importData.englishTestProvider
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.ENGLISH_TEST_TYPE);
            onChange(Q.ENGLISH_TEST_TYPE, isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE) ? englishTestType : '');
          }}
        />
      )}

      {data.englishTestNotReqdReasonVisible && (
        <RadioOptionsWithConfirm
          key={Q.ENGLISH_TEST_NOT_REQD_REASON}
          id={Q.ENGLISH_TEST_NOT_REQD_REASON}
          label={data.englishTestNotReqdReasonConfig?.label}
          inputHelperText={data.englishTestNotReqdReasonConfig?.helpText}
          onClick={question => {
            const char = !data.isStudentFromEnglishSpeakingCountry ? question.char + 1 : question.char;
            const answer = charsValueMap[char];
            onChange(Q.ENGLISH_TEST_NOT_REQD_REASON, answer);
            if (data.englishTestNotReqdReasonOther) {
              onChange(Q.ENGLISH_TEST_NOT_REQD_REASON_OTHER, '');
            }
            if (data.qualification) {
              onChange(Q.QUALIFICATION, '');
            }
            if (data.qualificationOther) {
              onChange(Q.QUALIFICATION_OTHER, '');
            }
          }}
          isInline={false}
          questions={notReqdReasons.map((reason, i) => ({ char: i + 1, ...reason }))}
        />
      )}

      {data.englishTestNotReqdReason === charsValueMap[6] && !data.hasEnglishTest ? (
        <InputWithConfirm
          id={`${Q.ENGLISH_TEST_NOT_REQD_REASON}_other`}
          label={data.englishTestNotReqdReasonOtherConfig.label}
          onChange={evt => onChange(Q.ENGLISH_TEST_NOT_REQD_REASON_OTHER, evt.currentTarget.value)}
          value={data.englishTestNotReqdReasonOther}
        />
      ) : null}

      {data.qualificationVisible && (
        <SelectWithOtherWithConfirm
          key={Q.QUALIFICATION}
          id={Q.QUALIFICATION}
          label={data.qualificationConfig?.label}
          options={QUALIFICATIONS}
          otherOptions={[
            {
              label: 'Other',
              value: 'Other'
            }
          ]}
          otherOptionLabel={data.qualificationOtherConfig?.label}
          value={data.qualification}
          onChange={handleQualificationOnChange}
          onChangeOtherText={evt => onChange(Q.QUALIFICATION_OTHER, evt.currentTarget.value)}
          inputHelperText={data.qualificationConfig?.helpText}
          otherValue={data.qualificationOther}
          disableOther={false}
        />
      )}
    </QuestionPage>
  );
};

export default PersonalQPage6;
